import React, { Component } from "react";
import { EchoRequest } from "../protos/echo_pb.js";
import { EchoServiceClient } from "../protos/echo_grpc_web_pb";
import TestStateIndicator from "./test-state-indicator";
import * as c from "../constants/troubleshooting";
import { Link } from "react-router-dom";
import bg from "../imgs/network-test.svg";

class Troubleshooting extends Component {
  constructor(props) {
    super(props);
    this.state = {
      restTestState: c.TestStates.NONE,
      restTestError: "",
      grpcTestState: c.TestStates.NONE,
      grpcTestError: "",
      webSocketTestState: c.TestStates.NONE,
      webSocketTestError: "",
    };
  }

  testConnections = () => {
    this.restTest();
    this.grpcTest();
    this.websocketTest();
  };

  // Test a REST connection
  async restTest() {
    // Start the loading icon
    this.setTestState(c.Protocols.REST, c.TestStates.LOADING);

    // Create REST request
    const request = new XMLHttpRequest();
    request.open(
      "GET",
      "https://" + c.CONFIG.testServer.url + ":" + c.CONFIG.testServer.rest.port
    );
    request.timeout = 5000; // 5 seconds

    // Pause for a bit to imply work
    await this.sleep(c.GOOD_FEELINGS_DELAY);

    // Send request
    request.send();
    request.onload = () => {
      console.log(request);
      if (request.status === 200) {
        // Strip off all linebreaks
        const data = request.response.split("<title>")[1].split("</title>")[0];
        console.debug("%s message data: %s", c.Protocols.REST, data);
        this.checkSuccess(
          data,
          c.CONFIG.testServer.rest.response,
          c.Protocols.REST
        );
      } else {
        console.log("error: %s %s", request.status, request.statusText);
        this.failed(c.Protocols.REST, "status code not 200");
      }
    };
    request.onerror = () => {
      this.failed(c.Protocols.REST, "an unknown error occured");
    };
    request.ontimeout = () => {
      this.failed(c.Protocols.REST, "request timed out");
      return;
    };
  }

  // Test gRPC connection
  async grpcTest() {
    // Start the loading icon
    this.setTestState(c.Protocols.GRPC, c.TestStates.LOADING);

    // Create gRPC client
    var client = new EchoServiceClient(
      "https://" + c.CONFIG.testServer.url + ":" + c.CONFIG.testServer.grpc.port
    );

    // Create gRPC request
    var request = new EchoRequest();
    request.setMessage(c.CONFIG.testServer.grpc.request);

    // Pause for a bit to imply work
    await this.sleep(c.GOOD_FEELINGS_DELAY);

    // Make gRPC call
    client.echo(request, {}, (err, response) => {
      // Log error
      if (err != null) {
        console.error(err);
        this.failed(c.Protocols.GRPC, err.message);
      } else {
        var data = response.getMessage();
        console.debug("%s message data: %s", c.Protocols.GRPC, data);
        this.checkSuccess(
          data,
          c.CONFIG.testServer.grpc.response,
          c.Protocols.GRPC
        );
      }
    });
  }

  // Test a websocket connection
  async websocketTest() {
    // Start the loading icon
    this.setTestState(c.Protocols.WEBSOCKET, c.TestStates.LOADING);

    // Pause for a bit to imply work
    await this.sleep(c.GOOD_FEELINGS_DELAY);

    // Create new websocket
    var ws = new WebSocket(
      "wss://" +
        c.CONFIG.testServer.url +
        ":" +
        c.CONFIG.testServer.websocket.port
    );

    // Event when websocket connects
    ws.onopen = () => {
      ws.send(c.CONFIG.testServer.websocket.request);
    };

    // Event when websocket receives message
    ws.onmessage = (e) => {
      const data = e.data;
      console.debug("%s message data: %s", c.Protocols.WEBSOCKET, data);

      // Just the connect message, ignore
      if (data === "Connection established.") {
        return;
      }

      this.checkSuccess(
        data,
        c.CONFIG.testServer.websocket.response,
        c.Protocols.WEBSOCKET
      );
    };

    ws.ontimeout = () => {
      this.failed(c.Protocols.WEBSOCKET, "connection timed out");
    };

    // Event when websocket encounters error
    ws.onerror = (err) => {
      console.error(err);
      this.failed(c.Protocols.WEBSOCKET, "An unknown error occured");
      ws.close();
    };
  }

  // Checks to see if the received response matches the expected response
  checkSuccess = (received, expected, protocol) => {
    if (received === expected) {
      this.passed(protocol);
    } else {
      console.error(
        "protocol: %s received: %s expected: %s",
        protocol,
        received,
        expected
      );
      this.failed(protocol, "reponse didn't match expected value");
    }
  };

  // Signal that the test passed
  passed = (protocol) => {
    console.log("%s: PASSED 😊", protocol);
    this.setTestState(protocol, c.TestStates.SUCCESS);
  };

  // Signal that the test failed
  failed = (protocol, reason) => {
    console.error("%s: FAILED 😞 reason: %s", protocol, reason);
    this.setTestError(protocol, reason);
    this.setTestState(protocol, c.TestStates.FAIL);
  };

  // Set the error message of the protocol
  setTestError = (protocol, reason) => {
    console.debug("Setting protocol %s to error %s", protocol, reason);
    switch (protocol) {
      case c.Protocols.REST:
        this.setState({ restTestError: reason });
        break;
      case c.Protocols.GRPC:
        this.setState({ grpcTestError: reason });
        break;
      case c.Protocols.WEBSOCKET:
        this.setState({ webSocketTestError: reason });
        break;
      default:
        console.error("Unrecognized protocol for setTestError");
    }
  };

  // Set the state of the protocol
  setTestState = (protocol, state) => {
    console.debug("Setting protocol %s to state %s", protocol, state);
    switch (protocol) {
      case c.Protocols.REST:
        this.setState({ restTestState: state });
        break;
      case c.Protocols.GRPC:
        this.setState({ grpcTestState: state });
        break;
      case c.Protocols.WEBSOCKET:
        this.setState({ webSocketTestState: state });
        break;
      default:
        console.error("Unrecognized protocol for setTestState");
    }
  };

  // Sleep for given time in milliseconds
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Render the page
  render() {
    return (
      <div className="test-wrapper">
        <button
          onClick={(e) => {
            this.testConnections();
            e.target.parentNode.classList.add("clicked");
          }}
        >
          {this.props.button}
        </button>
        <div
          className="test-results"
          onClick={(e) => {
            e.target.parentNode.classList.remove("clicked");
          }}
        >
          <div>
            <button
              className="close"
              onClick={(e) => {
                e.target.parentNode.parentNode.parentNode.classList.remove(
                  "clicked"
                );
              }}
            >
              ×
            </button>
            <h1>Network Test</h1>
            <div>
              <span
                className={
                  "indicator " +
                  (this.state.restTestError
                    ? "error"
                    : this.state.restTestState === "success"
                    ? "success"
                    : "")
                }
              ></span>
              <span
                className={
                  "indicator " +
                  (this.state.grpcTestError
                    ? "error"
                    : this.state.grpcTestState === "success"
                    ? "success"
                    : "")
                }
              ></span>
              <span
                className={
                  "indicator " +
                  (this.state.webSocketTestError
                    ? "error"
                    : this.state.webSocketTestState === "success"
                    ? "success"
                    : "")
                }
              ></span>
              <img src={bg} />
            </div>
            <div>
              <h2>REST test</h2>
              <span>Checking standard transfer connection.</span>
              <TestStateIndicator
                testState={this.state.restTestState}
                testError={this.state.restTestError}
              />
            </div>
            <div>
              <h2>gRPC test</h2>
              <span>Checking alternative functional connections.</span>
              <TestStateIndicator
                testState={this.state.grpcTestState}
                testError={this.state.grpcTestError}
              />
            </div>
            <div>
              <h2>WebSocket test</h2>
              <span> Checking your interactive browser connection.</span>
              <TestStateIndicator
                testState={this.state.webSocketTestState}
                testError={this.state.webSocketTestError}
              />
            </div>
{console.log('hi',this.state)}
            {this.state.restTestError ||
            this.state.grpcTestError ||
            this.state.webSocketTestError ? (
              <div>
                <hr />
                <p>
                  Uh Oh, something is jammed up in the connection between your
                  computer and Project Ares.
                </p>
                <p>
                  Don’t worry though. We’ll work with you to resolve the issue.{" "}
                </p>
                <p>
                  Email these results to our Project Ares Support Team <a
                    href={
                      "mailto:csd@circadence.com?subject=Network Connection Test Failed&body=Hello Project Ares Support Team:%0D%0AThis is the results of my Project Ares Connection Test.  I need your help to address this.%0D%0A%0D%0AREST:" +
                      (this.state.restTestError
                        ? this.state.restTestError
                        : "success") +
                      "%0D%0AgRPC:" +
                      (this.state.grpcTestError
                        ? this.state.grpcTestError
                        : "success") +
                      "%0D%0AWebSocket:" +
                      (this.state.webSocketTestError
                        ? this.state.webSocketTestError
                        : "success")
                    }
                  >(csd@circadence.com)</a> and they’ll get back to you shortly with next steps.
                </p>
                <p>Page version: 0.1.0</p>
              </div>
            ) : (
              this.state.restTestState=="success" &&
            this.state.grpcTestState=="success" &&
              this.state.webSocketTestState=="success"? <div>
              <hr /><p>Good News!  All the connections between your computer and Project Ares look good.
              You are ready to get started.
              </p></div>:""
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default Troubleshooting;
